import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Search, Filter, MoreVertical, Edit2, Trash2, ExternalLink, Eye, EyeOff, LayoutTemplate, X, Image as ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import InputField from "../../components/common/InputField";

const GlassCard = ({ children, className = "" }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const Modal = ({ isOpen, onClose, title, children }) => {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cht-absolute cht-inset-0 cht-bg-slate-900/40 cht-backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="cht-relative cht-w-full cht-max-w-2xl cht-bg-white cht-rounded-[32px] cht-p-8 cht-shadow-[0_20px_60px_rgba(0,0,0,0.1)] cht-max-h-[90vh] cht-overflow-y-auto"
          >
            <div className="cht-flex cht-justify-between cht-items-center cht-mb-6">
              <h2 className="cht-text-2xl cht-font-black cht-text-[#1e293b]">{title}</h2>
              <button onClick={onClose} className="cht-p-2 cht-bg-gray-50 cht-rounded-xl cht-text-gray-500 hover:cht-bg-gray-100 transition-colors">
                <X size={20} />
              </button>
            </div>
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// --- Dummy Data ---
const INITIAL_POSTS = [
  { id: "POST-001", title: "Getting Started with React in 2026", author: "David Osiota", status: "Published", date: "May 01, 2026", views: 1240, thumbnail: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?q=80&w=150&auto=format&fit=crop", content: "React continues to evolve. In this post, we explore the latest features..." },
  { id: "POST-002", title: "Mastering Tailwind CSS Animations", author: "Sarah Jenkins", status: "Draft", date: "May 02, 2026", views: 0, thumbnail: "https://images.unsplash.com/photo-1542831371-29b0f74f9713?q=80&w=150&auto=format&fit=crop", content: "Tailwind makes styling easy, but animations can be tricky..." },
  { id: "POST-003", title: "The Future of Web Development", author: "Michael Chen", status: "Published", date: "Apr 28, 2026", views: 3450, thumbnail: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=150&auto=format&fit=crop", content: "AI is changing everything we know about coding..." },
  { id: "POST-004", title: "Understanding Node.js Performance", author: "Emily Rodriguez", status: "Published", date: "Apr 15, 2026", views: 890, thumbnail: "https://images.unsplash.com/photo-1618477388954-7852f32655ec?q=80&w=150&auto=format&fit=crop", content: "Let's dive deep into the V8 engine and the event loop..." },
  { id: "POST-005", title: "10 Tips for Better UX Design", author: "James Wilson", status: "Draft", date: "May 05, 2026", views: 0, thumbnail: "https://images.unsplash.com/photo-1561070791-2526d30994b5?q=80&w=150&auto=format&fit=crop", content: "User experience matters more than ever..." },
  { id: "POST-006", title: "Building Scalable Microservices", author: "David Osiota", status: "Published", date: "Apr 10, 2026", views: 2100, thumbnail: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=150&auto=format&fit=crop", content: "Microservices architecture is the way to go for large scale apps..." },
  { id: "POST-007", title: "Introduction to Web3", author: "Alice Wonderland", status: "Draft", date: "May 06, 2026", views: 0, thumbnail: "https://images.unsplash.com/photo-1621504450181-5d356f61d307?q=80&w=150&auto=format&fit=crop", content: "A beginner's guide to the decentralized web..." },
  { id: "POST-008", title: "Deploying React Apps on Vercel", author: "Sarah Jenkins", status: "Published", date: "Mar 22, 2026", views: 5600, thumbnail: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=150&auto=format&fit=crop", content: "Learn how to easily deploy your frontend apps..." },
  { id: "POST-009", title: "The Power of Serverless Computing", author: "Michael Chen", status: "Published", date: "Feb 15, 2026", views: 1800, thumbnail: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=150&auto=format&fit=crop", content: "Why serverless is taking over the cloud industry..." },
  { id: "POST-010", title: "GraphQL vs REST", author: "Emily Rodriguez", status: "Draft", date: "May 08, 2026", views: 0, thumbnail: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=150&auto=format&fit=crop", content: "Which API paradigm should you choose in 2026?..." },
  { id: "POST-011", title: "Optimizing Web Fonts", author: "James Wilson", status: "Published", date: "Jan 30, 2026", views: 320, thumbnail: "https://images.unsplash.com/photo-1526379095098-d400fd0bf935?q=80&w=150&auto=format&fit=crop", content: "How to make your site load faster without compromising typography..." },
  { id: "POST-012", title: "Fullstack Development Guide", author: "David Osiota", status: "Published", date: "Dec 12, 2025", views: 8900, thumbnail: "https://images.unsplash.com/photo-1550439062-609e1531270e?q=80&w=150&auto=format&fit=crop", content: "A complete roadmap to becoming a fullstack developer..." },
];

const AdminBlog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState(() => {
    const saved = localStorage.getItem('blog_posts');
    if (saved) return JSON.parse(saved);
    localStorage.setItem('blog_posts', JSON.stringify(INITIAL_POSTS));
    return INITIAL_POSTS;
  });

  useEffect(() => {
    const saved = localStorage.getItem('blog_posts');
    if (saved) {
      setPosts(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('blog_posts', JSON.stringify(posts));
  }, [posts]);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Date Range Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // edit, view, delete
  const [selectedPost, setSelectedPost] = useState(null);
  const [formData, setFormData] = useState({ title: "", author: "Admin", status: "Draft", thumbnail: "", content: "" });

  const filteredPosts = useMemo(() => {
    const filtered = posts.filter(post => {
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            post.author.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === "All" || post.status === filterStatus;
      
      let matchesDate = true;
      if (startDate || endDate) {
        const pDate = new Date(post.date);
        if (!isNaN(pDate.getTime())) {
          pDate.setHours(0, 0, 0, 0);
          if (startDate) {
            const sDate = new Date(startDate);
            sDate.setHours(0, 0, 0, 0);
            if (pDate < sDate) matchesDate = false;
          }
          if (endDate) {
            const eDate = new Date(endDate);
            eDate.setHours(0, 0, 0, 0);
            if (pDate > eDate) matchesDate = false;
          }
        }
      }
      return matchesSearch && matchesStatus && matchesDate;
    });
    return filtered;
  }, [posts, searchQuery, filterStatus, startDate, endDate]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, startDate, endDate]);

  const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
  const currentPosts = filteredPosts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalPosts = posts.length;
  const publishedPosts = posts.filter(p => p.status === "Published").length;
  const draftPosts = posts.filter(p => p.status === "Draft").length;
  const totalViews = posts.reduce((acc, curr) => acc + curr.views, 0);

  const openModal = (mode, post) => {
    setModalMode(mode);
    setSelectedPost(post);
    if (post && (mode === "edit" || mode === "view")) {
      setFormData({ ...post });
    }
    setIsModalOpen(true);
    setActiveDropdown(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
  };

  const handleSave = () => {
    if (modalMode === "edit") {
      setPosts(posts.map(p => p.id === selectedPost.id ? { ...p, ...formData } : p));
    }
    closeModal();
  };

  const handleDelete = () => {
    setPosts(posts.filter(p => p.id !== selectedPost.id));
    closeModal();
  };

  const toggleStatus = (post) => {
    const newStatus = post.status === "Published" ? "Draft" : "Published";
    setPosts(posts.map(p => p.id === post.id ? { ...p, status: newStatus } : p));
    setActiveDropdown(null);
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">Blog Management</h1>
          <p className="cht-text-gray-500 cht-font-medium">Create, edit, and manage platform blog content.</p>
        </div>
        <Button variant="primary" leftIcon={<Plus size={18} />} onClick={() => navigate('/staff/dashboard/blog/create')} className="cht-rounded-2xl">
          New Post
        </Button>
      </header>

      {/* KPI Grid */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-blue-50 cht-text-blue-600 cht-rounded-2xl"><LayoutTemplate size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Total Posts</p><h4 className="cht-text-2xl cht-font-black">{totalPosts}</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl"><Eye size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Total Views</p><h4 className="cht-text-2xl cht-font-black">{totalViews.toLocaleString()}</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl"><FileText size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Published</p><h4 className="cht-text-2xl cht-font-black">{publishedPosts}</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl"><EyeOff size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Drafts</p><h4 className="cht-text-2xl cht-font-black">{draftPosts}</h4></div>
        </GlassCard>
      </div>

      {/* Filters and Date Range */}
      <div className="cht-space-y-4">
        {/* Date Range Selector */}
        <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-50 cht-shadow-sm">
          <div className="cht-flex cht-items-center cht-gap-2">
            <LayoutTemplate size={18} className="cht-text-gray-400" />
            <span className="cht-text-sm cht-font-bold cht-text-gray-600">Filter by Date:</span>
          </div>
          <div className="cht-flex cht-items-center cht-gap-3">
            <div className="cht-relative">
              <input 
                type="date" 
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-2.5 cht-px-4 cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/10"
                title="Start Date"
              />
            </div>
            <span className="cht-text-gray-400 cht-font-medium">to</span>
            <div className="cht-relative">
              <input 
                type="date" 
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-2.5 cht-px-4 cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/10"
                title="End Date"
              />
            </div>
            {(startDate || endDate) && (
              <button 
                onClick={() => { setStartDate(""); setEndDate(""); }} 
                className="cht-p-2 cht-bg-rose-50 hover:cht-bg-rose-100 cht-text-rose-500 cht-rounded-xl cht-transition-colors"
                title="Clear Dates"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-50 cht-shadow-sm">
          <div className="cht-relative cht-flex-1 cht-w-full">
            <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Search by title or author..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3.5 cht-pl-12 cht-pr-4 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all cht-outline-none"
            />
          </div>
          <div className="cht-flex cht-items-center cht-gap-2 cht-w-full md:cht-w-auto">
            {["All", "Published", "Draft"].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`cht-flex-1 md:cht-flex-none cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
                  filterStatus === status ? "cht-bg-[#1e293b] cht-text-white" : "cht-bg-gray-50 cht-text-gray-400 hover:cht-bg-gray-100"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posts Table */}
      <GlassCard className="cht-rounded-[40px] cht-overflow-visible">
        <div className="cht-overflow-x-auto">
          <table className="cht-w-full">
            <thead>
              <tr className="cht-border-b cht-border-gray-50">
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Post Info</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Author</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Status</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Date</th>
                <th className="cht-px-8 cht-py-6 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {currentPosts.map((post) => (
                <tr key={post.id} className="hover:cht-bg-gray-50/50 cht-transition-colors group">
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-flex cht-items-center cht-gap-4">
                      <div className="cht-w-16 cht-h-12 cht-rounded-[12px] cht-overflow-hidden cht-border cht-border-gray-100">
                        <img src={post.thumbnail} alt={post.title} className="cht-w-full cht-h-full cht-object-cover" />
                      </div>
                      <div className="cht-space-y-1 cht-max-w-[250px]">
                        <h4 className="cht-text-sm cht-font-black cht-text-gray-900 cht-truncate" title={post.title}>
                          {post.title}
                        </h4>
                        <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-text-gray-400 cht-font-medium">
                          {post.views > 0 ? `${post.views.toLocaleString()} views` : 'No views yet'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <p className="cht-text-sm cht-font-bold cht-text-gray-700">{post.author}</p>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <span className={`cht-px-3 cht-py-1.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest ${
                      post.status === 'Published' ? 'cht-bg-emerald-100 cht-text-emerald-700' : 'cht-bg-amber-100 cht-text-amber-700'
                    }`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <p className="cht-text-xs cht-font-medium cht-text-gray-500">{post.date}</p>
                  </td>
                  <td className="cht-px-8 cht-py-6 cht-text-right">
                    <div className="cht-relative cht-inline-block">
                      <button 
                        onClick={() => setActiveDropdown(activeDropdown === post.id ? null : post.id)}
                        className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-gray-400 hover:cht-bg-gray-100 hover:cht-text-gray-900 transition-colors"
                      >
                        <MoreVertical size={20} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === post.id && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="cht-absolute cht-right-0 cht-mt-2 cht-w-48 cht-bg-white cht-rounded-2xl cht-shadow-[0_20px_60px_rgba(0,0,0,0.1)] cht-border cht-border-gray-50 cht-p-2 cht-z-[9999]"
                          >
                            <button onClick={() => openModal('view', post)} className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-rounded-xl transition-colors">
                              <ExternalLink size={16} /> View Post
                            </button>
                            <button onClick={() => openModal('edit', post)} className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-rounded-xl transition-colors">
                              <Edit2 size={16} /> Edit Post
                            </button>
                            <div className="cht-h-px cht-bg-gray-100 cht-my-1" />
                            {post.status === 'Published' ? (
                              <button onClick={() => toggleStatus(post)} className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-amber-600 hover:cht-bg-amber-50 cht-rounded-xl transition-colors">
                                <EyeOff size={16} /> Unpublish
                              </button>
                            ) : (
                              <button onClick={() => toggleStatus(post)} className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-emerald-600 hover:cht-bg-emerald-50 cht-rounded-xl transition-colors">
                                <Eye size={16} /> Publish
                              </button>
                            )}
                            <button onClick={() => openModal('delete', post)} className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-xl transition-colors">
                              <Trash2 size={16} /> Delete Post
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredPosts.length === 0 && (
            <div className="cht-py-20 cht-text-center">
              <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4 cht-text-gray-300">
                <FileText size={32} />
              </div>
              <h3 className="cht-text-lg cht-font-black cht-text-gray-900">No posts found</h3>
              <p className="cht-text-sm cht-text-gray-400 cht-font-medium">Try adjusting your filters or search terms.</p>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="cht-px-8 cht-py-4 cht-border-t cht-border-gray-50 cht-flex cht-items-center cht-justify-between">
              <span className="cht-text-xs cht-font-bold cht-text-gray-400">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredPosts.length)} of {filteredPosts.length} entries
              </span>
              <div className="cht-flex cht-items-center cht-gap-2">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="cht-p-2 cht-rounded-xl cht-bg-gray-50 cht-text-gray-500 hover:cht-bg-gray-100 disabled:cht-opacity-50 transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="cht-flex cht-gap-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`cht-w-8 cht-h-8 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all ${currentPage === i + 1 ? 'cht-bg-[#1e293b] cht-text-white' : 'cht-bg-transparent cht-text-gray-500 hover:cht-bg-gray-50'}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="cht-p-2 cht-rounded-xl cht-bg-gray-50 cht-text-gray-500 hover:cht-bg-gray-100 disabled:cht-opacity-50 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Invisible overlay to close dropdowns */}
      {activeDropdown && (
        <div 
          className="cht-fixed cht-inset-0 cht-z-[50]" 
          onClick={() => setActiveDropdown(null)} 
        />
      )}

      {/* Modals */}
      <Modal 
        isOpen={isModalOpen && modalMode === 'edit'} 
        onClose={closeModal} 
        title="Edit Post"
      >
        <div className="cht-space-y-6">
          <InputField 
            label="Post Title" 
            placeholder="Enter post title" 
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            leftIcon={<FileText size={18} />}
          />
          <InputField 
            label="Author" 
            placeholder="Enter author name" 
            value={formData.author}
            onChange={(e) => setFormData({...formData, author: e.target.value})}
          />
          <InputField 
            label="Thumbnail URL" 
            placeholder="Enter image URL" 
            value={formData.thumbnail}
            onChange={(e) => setFormData({...formData, thumbnail: e.target.value})}
            leftIcon={<ImageIcon size={18} />}
          />
          
          <div className="cht-flex cht-flex-col cht-gap-2">
            <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-[0.2em] cht-ml-2">Content</label>
            <textarea 
              rows={6}
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              className="cht-w-full cht-bg-gray-50 cht-border-2 cht-border-transparent focus:cht-border-[#3e57da]/20 focus:cht-bg-white cht-rounded-[18px] cht-p-4 cht-text-sm cht-font-bold cht-text-[#1e293b] cht-outline-none cht-transition-all cht-resize-none"
              placeholder="Write your post content here..."
            />
          </div>

          <div className="cht-flex cht-flex-col cht-gap-2">
            <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-[0.2em] cht-ml-2">Status</label>
            <div className="cht-flex cht-gap-4">
              <button 
                onClick={() => setFormData({...formData, status: "Draft"})}
                className={`cht-flex-1 cht-py-3 cht-rounded-xl cht-text-sm cht-font-black cht-transition-all ${formData.status === 'Draft' ? 'cht-bg-[#1e293b] cht-text-white' : 'cht-bg-gray-50 cht-text-gray-500 hover:cht-bg-gray-100'}`}
              >
                Draft
              </button>
              <button 
                onClick={() => setFormData({...formData, status: "Published"})}
                className={`cht-flex-1 cht-py-3 cht-rounded-xl cht-text-sm cht-font-black cht-transition-all ${formData.status === 'Published' ? 'cht-bg-[#3e57da] cht-text-white' : 'cht-bg-gray-50 cht-text-gray-500 hover:cht-bg-gray-100'}`}
              >
                Published
              </button>
            </div>
          </div>

          <div className="cht-pt-6 cht-flex cht-gap-4 cht-justify-end">
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button variant="primary" onClick={handleSave}>Save Changes</Button>
          </div>
        </div>
      </Modal>

      <Modal 
        isOpen={isModalOpen && modalMode === 'view'} 
        onClose={closeModal} 
        title="View Post"
      >
        {selectedPost && (
          <div className="cht-space-y-6">
            <div className="cht-w-full cht-h-48 cht-rounded-2xl cht-overflow-hidden">
              <img src={selectedPost.thumbnail} alt={selectedPost.title} className="cht-w-full cht-h-full cht-object-cover" />
            </div>
            <div>
              <h1 className="cht-text-2xl cht-font-black cht-text-[#1e293b] cht-mb-2">{selectedPost.title}</h1>
              <div className="cht-flex cht-items-center cht-gap-4 cht-text-sm cht-font-bold cht-text-gray-500">
                <span>By {selectedPost.author}</span>
                <span>•</span>
                <span>{selectedPost.date}</span>
                <span>•</span>
                <span className={`cht-px-2 cht-py-1 cht-rounded-md cht-text-[10px] cht-uppercase cht-tracking-widest ${selectedPost.status === 'Published' ? 'cht-bg-emerald-100 cht-text-emerald-700' : 'cht-bg-amber-100 cht-text-amber-700'}`}>
                  {selectedPost.status}
                </span>
              </div>
            </div>
            <div className="cht-prose cht-max-w-none cht-text-gray-700">
              <p>{selectedPost.content}</p>
            </div>
            <div className="cht-pt-6 cht-flex cht-justify-end">
              <Button variant="primary" onClick={() => openModal('edit', selectedPost)}>Edit Post</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal 
        isOpen={isModalOpen && modalMode === 'delete'} 
        onClose={closeModal} 
        title="Delete Post"
      >
        <div className="cht-text-center cht-py-6">
          <div className="cht-w-16 cht-h-16 cht-bg-rose-100 cht-text-rose-600 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4">
            <Trash2 size={24} />
          </div>
          <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mb-2">Are you sure?</h3>
          <p className="cht-text-gray-500 cht-font-medium cht-max-w-sm cht-mx-auto">
            Do you really want to delete <strong>"{selectedPost?.title}"</strong>? This action cannot be undone.
          </p>
          <div className="cht-mt-8 cht-flex cht-gap-4 cht-justify-center">
            <Button variant="outline" onClick={closeModal} className="cht-px-8">Cancel</Button>
            <button 
              onClick={handleDelete}
              className="cht-px-8 cht-py-3 cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-white cht-rounded-xl cht-font-bold cht-transition-colors"
            >
              Delete Post
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminBlog;
